import { useEffect, useState, useRef, Fragment, lazy, Suspense, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { Building2, Server, ScrollText, Plus, LogOut, Loader2, Check, X, CreditCard, Activity, Settings as SettingsIcon, Download, LayoutDashboard, Mail, Lock, Eye, EyeOff, Pause, Play, ExternalLink, Languages, Send, Save, Pencil, LineChart as LineChartIcon } from 'lucide-react';
import { IOSentraLogo } from './IOSentraLogo';
import { systemApi, setToken, getToken, type SystemUser, type Tenant, type FilteredAuditEntry, type FleetAgent, type Plan, type MetricsPoint } from '@/api';
import { CustomerDetail } from '@/CustomerDetail';
import { useToast, ConfirmDestructive, StatusPill, KpiStat } from '@/components/ui';
import { DataTable, PageHeader, type Column } from '@/components/DataTable';
import { ChartCard, CHART } from '@/components/chart-card';

// recharts is heavy (~200kB gzip); code-split so the Services KPIs paint first.
const TrendArea = lazy(() => import('@/components/charts').then((m) => ({ default: m.TrendArea })));
import { activityIcon } from '@/components/activity-icon';
import { Dashboard } from '@/Dashboard';
import i18n, { setAdminLanguage } from '@/i18n';

// EN / עברית language toggle for the control plane (RTL flips on Hebrew).
function LanguageToggle() {
  const cur = (i18n.language || 'en').split('-')[0];
  const Btn = ({ lng, label }: { lng: string; label: string }) => (
    <button
      onClick={() => setAdminLanguage(lng)}
      className={`flex-1 rounded px-2 py-1 transition-colors ${cur === lng ? 'bg-white/10 text-fg' : 'text-muted hover:text-fg'}`}
    >{label}</button>
  );
  return (
    <div className="flex items-center gap-1 rounded-md bg-white/5 p-0.5 text-xs ring-1 ring-white/10">
      <Languages size={13} className="ms-1 shrink-0 text-muted" />
      <Btn lng="en" label="EN" />
      <Btn lng="he" label="עברית" />
    </div>
  );
}

// Audit action shown as an icon + label pill (shared icon map with the Overview
// activity feed) so the log reads at a glance.
function AuditActionBadge({ action }: { action: string }) {
  const { Icon, tone } = activityIcon(action);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2 py-0.5 text-xs ring-1 ring-white/10">
      <Icon size={12} className={tone} /> <span className="text-fg">{action}</span>
    </span>
  );
}

// Friendly labels for the audit "resource" column. Raw table names like
// `system_settings` + a truncated UUID read poorly; map to human labels and show
// the id only when it's actually meaningful (not a singleton config row).
const RESOURCE_LABELS: Record<string, string> = {
  system_settings: 'System settings',
  system_user: 'Admin user',
  system_messaging_providers: 'Messaging provider',
  system_messaging_templates: 'Messaging template',
  tenant: 'Customer',
  plan: 'Plan',
  subscription: 'Subscription',
  api_key: 'API key',
  apikey: 'API key',
};
// Resource types that are singletons / self-describing, so the id adds no value.
const RESOURCE_HIDE_ID = new Set(['system_settings']);

function formatResource(resourceType: string | null, resourceId: string | null, details?: Record<string, unknown>): string {
  if (!resourceType) return '—';
  const label = RESOURCE_LABELS[resourceType] ?? resourceType.replace(/_/g, ' ');
  if (RESOURCE_HIDE_ID.has(resourceType) || !resourceId) return label;
  // Prefer a human name/slug from the audit details over a raw UUID.
  const named = (details?.name ?? details?.slug ?? details?.email ?? details?.type) as string | undefined;
  if (named) return `${label} · ${named}`;
  // Otherwise show a short id (first UUID segment) so it's still traceable.
  const shortId = String(resourceId).split('-')[0];
  return `${label} · ${shortId}`;
}

const SUPPORT_EMAIL = (import.meta.env.VITE_SUPPORT_EMAIL as string) || 'support@iosentra.io';
const API_BASE = (import.meta.env.VITE_API_URL as string) || '';

// Pull a friendly server message out of an axios error (falls back to copy).
const friendlyError = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
  ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error
  ?? fallback;

type Section = 'overview' | 'tenants' | 'billing' | 'fleet' | 'services' | 'audit' | 'settings';

export function App() {
  const [user, setUser] = useState<SystemUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore session (token in localStorage) by calling /system/me.
  useEffect(() => {
    if (!getToken()) { setLoading(false); return; }
    systemApi.me().then(setUser).catch(() => setToken(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grid h-full place-items-center text-muted"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Login onLogin={setUser} />;
  return <Console user={user} onLogout={() => { setToken(null); setUser(null); }} />;
}

// Quiet, slowly-rotating product lines — confident framing for the control
// plane, no fake metrics (mirrors the operator app's sign-in).
const TAGLINE_KEYS = [
  'app.login.taglines.0',
  'app.login.taglines.1',
  'app.login.taglines.2',
  'app.login.taglines.3',
];
type ServiceState = 'up' | 'down' | 'checking';

// Super-admin sign-in. Same full-bleed ambient hero + floating glass card as the
// operator console (apps/web LoginPage), so 5174 and 5173 feel like one product.
function Login({ onLogin }: { onLogin: (u: SystemUser) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [service, setService] = useState<ServiceState>('checking');
  const [tagline, setTagline] = useState(0);
  // 2FA sub-steps: 'verify' (user has TOTP), 'enroll' (required but not set up).
  const [twoFa, setTwoFa] = useState<{ mode: 'verify' | 'enroll'; challenge: string; qrDataUrl?: string; secret?: string } | null>(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = setInterval(() => setTagline((i) => (i + 1) % TAGLINE_KEYS.length), 5000);
    return () => clearInterval(id);
  }, []);

  // Real, unauthenticated service-status ping (no fake green dot).
  useEffect(() => {
    let alive = true;
    const ping = () => fetch(`${API_BASE}/health`, { cache: 'no-store' })
      .then((r) => { if (alive) setService(r.ok ? 'up' : 'down'); })
      .catch(() => { if (alive) setService('down'); });
    ping();
    const id = setInterval(ping, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await systemApi.login(email, password);
      if (res.accessToken && res.user) { setToken(res.accessToken); onLogin(res.user); return; }
      // 2FA required.
      if (res.requiresTotp && res.challenge) { setTwoFa({ mode: 'verify', challenge: res.challenge }); setCode(''); return; }
      if (res.requiresTotpEnrollment && res.challenge) {
        // "Require 2FA for all super-admins" is on and this admin hasn't enrolled
        // → force TOTP setup: fetch the QR, then confirm to complete login.
        const en = await systemApi.enrollChallengeStart(res.challenge);
        setTwoFa({ mode: 'enroll', challenge: res.challenge, qrDataUrl: en.qrDataUrl, secret: en.secret }); setCode(''); return;
      }
      setError(t('app.login.error', 'Sign in failed'));
    } catch (err) {
      setError(friendlyError(err, t('app.login.error')));
    } finally { setBusy(false); }
  };

  const submit2fa = async (e: FormEvent) => {
    e.preventDefault();
    if (!twoFa) return;
    setBusy(true); setError(null);
    try {
      if (twoFa.mode === 'verify') {
        const res = await systemApi.loginVerifyTotp(twoFa.challenge, code.trim());
        setToken(res.accessToken); onLogin(res.user);
      } else {
        const res = await systemApi.enrollChallengeConfirm(twoFa.challenge, code.trim());
        if (res.accessToken && res.user) { setToken(res.accessToken); onLogin(res.user); }
        else setError('Enrollment did not complete. Try signing in again.');
      }
    } catch {
      setError('That code is incorrect. Try again.');
    } finally { setBusy(false); }
  };

  const capsCheck = (e: React.KeyboardEvent) => {
    const on = e.getModifierState?.('CapsLock');
    if (typeof on === 'boolean') setCapsOn(on);
  };

  return (
    <div className="flex min-h-full flex-col">

      <main className="grid flex-1 place-items-center px-6 py-10">
        <div className="w-full max-w-md">
          {/* Brand lockup */}
          <div className="mb-8 flex items-center justify-center gap-3.5">
            <span className="grid h-16 w-16 place-items-center rounded-2xl bg-primary/10 ring-1 ring-primary/25 shadow-[0_0_40px_-6px_rgba(56,142,255,0.6)]">
              <IOSentraLogo size={40} />
            </span>
            <div className="leading-tight">
              <div className="text-3xl font-bold tracking-tight">IOSentra</div>
              <div className="text-sm text-muted">{t('app.controlPlane')}</div>
            </div>
          </div>

          {/* The glass sign-in card, floating over the hero. */}
          <div className="tk-modal p-8">
            <h1 className="text-2xl font-semibold">{t('app.login.title')}</h1>
            <p className="mt-1 text-sm text-muted">{t('app.login.subtitle')}</p>

            {twoFa ? (
              <form onSubmit={submit2fa} className="mt-7 space-y-5">
                {twoFa.mode === 'enroll' && (
                  <div className="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                    <p className="text-sm text-fg">Two-factor is required for super-admins. Scan this with an authenticator app, then enter the code.</p>
                    {twoFa.qrDataUrl && <img src={twoFa.qrDataUrl} alt="TOTP QR" width={168} height={168} className="mx-auto rounded-lg bg-white p-2" />}
                    {twoFa.secret && <p className="break-all text-center font-mono text-xs text-muted">{twoFa.secret}</p>}
                  </div>
                )}
                {twoFa.mode === 'verify' && (
                  <p className="text-sm text-muted">Enter the 6-digit code from your authenticator app.</p>
                )}
                <div>
                  <label htmlFor="totp" className="mb-1.5 block text-sm text-muted">Authentication code</label>
                  <input
                    id="totp" inputMode="numeric" autoComplete="one-time-code" autoFocus required
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="tk-input w-full text-center font-mono text-lg tracking-[0.4em]" placeholder="000000"
                  />
                </div>
                {error && <div className="rounded-md border border-critical/30 bg-critical/10 px-3 py-2 text-sm text-critical">{error}</div>}
                <button type="submit" className="tk-btn-primary w-full py-2.5 text-base" disabled={busy || code.length !== 6}>
                  {busy ? '…' : (twoFa.mode === 'enroll' ? 'Confirm & sign in' : 'Verify')}
                </button>
                <button type="button" onClick={() => { setTwoFa(null); setCode(''); setError(null); }} className="w-full text-center text-xs text-muted hover:text-fg">Back to sign in</button>
              </form>
            ) : (
            <form onSubmit={submit} className="mt-7 space-y-5">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm text-muted">{t('app.login.email')}</label>
                <div className="relative">
                  <Mail size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    id="email" type="email" autoComplete="username" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="tk-input py-2.5 pl-10 text-base" placeholder="you@iosentra.io"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="mb-1.5 block text-sm text-muted">{t('app.login.password')}</label>
                <div className="relative">
                  <Lock size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    id="password" type={showPw ? 'text' : 'password'} autoComplete="current-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    onKeyUp={capsCheck} onKeyDown={capsCheck}
                    className="tk-input py-2.5 px-10 text-base" placeholder="••••••••"
                  />
                  <button
                    type="button" onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? t('app.login.hidePassword') : t('app.login.showPassword')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-fg"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {capsOn && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-warning">
                    <span className="h-1.5 w-1.5 rounded-full bg-warning" /> {t('app.login.capsLock')}
                  </p>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-md border border-critical/30 bg-critical/10 px-3 py-2">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-critical" />
                  <p className="text-sm text-critical">{error}</p>
                </div>
              )}

              <button type="submit" className="tk-btn-primary w-full py-2.5 text-base" disabled={busy}>
                {busy ? (
                  <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> {t('app.login.signingIn')}</>
                ) : t('app.login.signIn')}
              </button>
            </form>
            )}
          </div>

          <p key={tagline} className="tk-tagline mt-6 text-center text-sm text-muted/90">{t(TAGLINE_KEYS[tagline])}</p>
          <p className="mt-6 text-center text-xs text-muted">
            {t('app.login.restricted')} <a href={`mailto:${SUPPORT_EMAIL}?subject=IOSentra%20control-plane%20access`} className="text-primary hover:opacity-80">{t('app.login.requestAccess')}</a>.
          </p>
        </div>
      </main>

      {/* Real footer — with a LIVE service-status dot from /health. */}
      <footer className="flex flex-col items-center justify-between gap-2 border-t border-white/5 px-6 py-4 text-xs text-muted sm:flex-row">
        <span className="flex items-center gap-1.5" title={`API: ${service}`}>
          {service === 'up' ? (
            <><span className="tk-pulse-dot h-1.5 w-1.5 rounded-full bg-ok" /> {t('app.login.status.operational')}</>
          ) : service === 'down' ? (
            <><span className="h-1.5 w-1.5 rounded-full bg-critical" /> {t('app.login.status.unreachable')}</>
          ) : (
            <><span className="h-1.5 w-1.5 rounded-full bg-muted" /> {t('app.login.status.checking')}</>
          )}
        </span>
        <span>© {new Date().getFullYear()} IOSentra · {t('app.controlPlane')}</span>
        <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-fg">{t('app.login.support')}</a>
      </footer>
    </div>
  );
}

// Sidebar IA — grouped like the operator app (a labelled section header over
// each cluster) while keeping the lightweight in-page section switcher.
const NAV_GROUPS: { titleKey: string; items: { key: Section; labelKey: string; icon: typeof Building2 }[] }[] = [
  { titleKey: '', items: [
    { key: 'overview', labelKey: 'app.nav.overview', icon: LayoutDashboard },
  ] },
  { titleKey: 'app.nav.groups.customers', items: [
    { key: 'tenants', labelKey: 'app.nav.tenants', icon: Building2 },
    // Billing = the revenue view (MRR/ARR + per-customer). The invoicing-PROVIDER
    // credentials live under Settings → Billing.
    { key: 'billing', labelKey: 'app.nav.billing', icon: CreditCard },
  ] },
  { titleKey: 'app.nav.groups.platform', items: [
    { key: 'services', labelKey: 'app.nav.services', icon: Activity },
    { key: 'fleet', labelKey: 'app.nav.fleet', icon: Server },
    { key: 'audit', labelKey: 'app.nav.audit', icon: ScrollText },
    { key: 'settings', labelKey: 'app.nav.settings', icon: SettingsIcon },
  ] },
];

function Console({ user, onLogout }: { user: SystemUser; onLogout: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [section, setSection] = useState<Section>('overview');
  return (
    <div className="flex h-full">
      {/* Solid (opaque) sidebar — the admin sidebar is a fixed full-width panel,
          so it must NOT be translucent like the app's floating hover-rail, or
          scrolling page content bleeds through it. */}
      <aside className="flex w-60 flex-col border-e border-white/10 bg-[rgb(20,28,46)]">
        {/* Brand lockup — gradient shield, matching the operator console. */}
        <div className="flex items-center gap-2 px-5 py-5 text-lg font-semibold tracking-tight">
          <IOSentraLogo size={28} className="shrink-0" />
          <div className="leading-tight">
            <span className="block">IOSentra</span>
            <span className="block text-[11px] font-normal text-muted">{t('app.controlPlane')}</span>
          </div>
        </div>

        <nav className="flex-1 space-y-3 overflow-y-auto px-3 pb-4">
          {NAV_GROUPS.map((g, gi) => (
            <div key={g.titleKey || gi} className="space-y-0.5">
              {g.titleKey && <div className="px-3 pt-1 text-[10px] font-medium uppercase tracking-wider text-muted/50">{t(g.titleKey)}</div>}
              {g.items.map((n) => {
                const active = section === n.key;
                return (
                  <button
                    key={n.key} onClick={() => setSection(n.key)}
                    className={`group relative flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition-colors ${active ? 'bg-white/10 text-fg shadow-inner' : 'text-muted hover:bg-white/5 hover:text-fg'}`}
                  >
                    {active && <span className="absolute start-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-gradient-to-b from-primary to-accent" />}
                    <n.icon size={16} className={active ? 'text-primary' : ''} /> {t(n.labelKey)}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 p-3 text-xs text-muted">
          <LanguageToggle />
          <div className="mt-2 truncate">{user.name || t('common:roles.superAdmin')}</div>
          <div className="truncate">{user.email}</div>
          <button onClick={onLogout} className="tk-btn-ghost mt-2 w-full justify-start px-2"><LogOut size={16} /> {t('common:actions.signOut')}</button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto px-6 py-6 2xl:px-10">
        {section === 'overview' && <Dashboard />}
        {section === 'tenants' && <Tenants />}
        {section === 'billing' && <BillingPage />}
        {section === 'services' && <Services />}
        {section === 'fleet' && <Fleet />}
        {section === 'audit' && <Audit />}
        {section === 'settings' && <SettingsView user={user} />}
      </main>
    </div>
  );
}

function Tenants() {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const { data: tenants, isLoading } = useQuery({ queryKey: ['tenants'], queryFn: systemApi.tenants });
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenants'] });
  if (selected) return <CustomerDetail tenantId={selected} onBack={() => { setSelected(null); invalidate(); }} />;

  const columns: Column<Tenant>[] = [
    { key: 'name', header: t('app.tenants.columns.name'), render: (row) => <button className="text-left font-medium hover:text-primary" onClick={() => setSelected(row.id)}>{row.name}</button> },
    { key: 'slug', header: t('app.tenants.columns.slug'), render: (row) => <span className="text-muted">{row.slug}</span> },
    { key: 'tier', header: t('app.tenants.columns.tier'), render: (row) => <span className="text-muted capitalize">{row.tier}</span> },
    { key: 'region', header: t('app.tenants.columns.region'), render: (row) => <span className="text-muted">{row.region ?? '—'}</span> },
    { key: 'status', header: t('app.tenants.columns.status'), render: (row) => <StatusPill status={row.status} /> },
    { key: 'logto', header: t('app.tenants.columns.identityOrg'), render: (row) => <span className="tk-num truncate text-xs text-muted">{row.logtoOrgId ?? '—'}</span> },
    { key: 'actions', header: '', align: 'right', render: (row) => <TenantActions tenant={row} onChanged={invalidate} /> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('app.tenants.title')}
        subtitle={t('app.tenants.subtitle')}
        action={<button className="tk-btn-primary" onClick={() => setAddOpen(true)}><Plus size={16} /> {t('app.tenants.new')}</button>}
      />

      {addOpen && <NewTenant onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); invalidate(); }} />}

      <DataTable
        columns={columns}
        rows={tenants}
        loading={isLoading}
        emptyIcon={Building2}
        emptyTitle={t('app.tenants.empty.title')}
        emptyHint={t('app.tenants.empty.hint')}
      />
    </div>
  );
}

// Per-row actions: Enable/Disable (suspend ↔ unsuspend — lock a customer out
// without deleting), Retry (failed/provisioning), and Delete (drops DB + identity
// org + row; active tenants need a typed-key confirm).
function TenantActions({ tenant, onChanged }: { tenant: Tenant; onChanged: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [busy, setBusy] = useState<'retry' | 'delete' | 'toggle' | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const toast = useToast();
  const canRetry = tenant.status === 'error' || tenant.status === 'provisioning';
  const suspended = tenant.status === 'suspended';

  const retry = async () => {
    setBusy('retry');
    try { await systemApi.retryTenant(tenant.id); toast.ok(t('app.tenants.toast.retrying', { name: tenant.name })); onChanged(); }
    catch (e) { toast.error(friendlyError(e, t('app.tenants.toast.retryFailed', { name: tenant.name }))); }
    finally { setBusy(null); }
  };
  const unsuspend = async () => {
    setBusy('toggle');
    try { await systemApi.unsuspend(tenant.id); toast.ok(t('app.tenants.toast.enabled', { name: tenant.name })); onChanged(); }
    catch (e) { toast.error(friendlyError(e, t('app.tenants.toast.enableFailed', { name: tenant.name }))); }
    finally { setBusy(null); }
  };
  const suspend = async () => {
    setBusy('toggle');
    try {
      await systemApi.suspend(tenant.id);
      toast.warn(t('app.tenants.toast.disabled', { name: tenant.name }));
      setConfirmSuspend(false); onChanged();
    } catch (e) { toast.error(friendlyError(e, t('app.tenants.toast.disableFailed', { name: tenant.name }))); }
    finally { setBusy(null); }
  };
  const del = async () => {
    setBusy('delete');
    try {
      await systemApi.deleteTenant(tenant.id, tenant.status === 'active');
      toast.ok(t('app.tenants.toast.deleted', { name: tenant.name }));
      setConfirmDel(false);
      onChanged();
    } catch (e) {
      toast.error(friendlyError(e, t('app.tenants.toast.deleteFailed', { name: tenant.name })));
    } finally { setBusy(null); }
  };

  return (
    <span className="flex items-center justify-end gap-1.5">
      {canRetry && (
        <button className="tk-btn-ghost px-2 py-1 text-xs" disabled={!!busy} onClick={retry}>
          {busy === 'retry' ? '…' : t('app.tenants.actions.retry')}
        </button>
      )}
      {/* Enable / Disable — suspend locks the customer out without deleting it. */}
      {suspended ? (
        <button className="tk-btn-ghost px-2 py-1 text-xs text-ok" disabled={!!busy} onClick={unsuspend} title={t('app.tenants.actions.enableTitle')}>
          <Play size={13} /> {busy === 'toggle' ? '…' : t('app.tenants.actions.enable')}
        </button>
      ) : (
        <button className="tk-btn-ghost px-2 py-1 text-xs text-warning" disabled={!!busy} onClick={() => setConfirmSuspend(true)} title={t('app.tenants.actions.disableTitle')}>
          <Pause size={13} /> {t('app.tenants.actions.disable')}
        </button>
      )}
      <button className="tk-btn-ghost px-2 py-1 text-xs text-critical" disabled={!!busy} onClick={() => setConfirmDel(true)}>
        {t('common:actions.delete')}
      </button>

      {confirmSuspend && (
        <ConfirmDestructive
          title={t('app.tenants.disableConfirm.title', { name: tenant.name })}
          confirmKey={tenant.name}
          confirmLabel={t('app.tenants.disableConfirm.label')}
          busy={busy === 'toggle'}
          onCancel={() => setConfirmSuspend(false)}
          onConfirm={suspend}
          body={
            <>
              <Trans t={t} i18nKey="app.tenants.disableConfirm.body" values={{ name: tenant.name }} components={{ b: <b /> }} />
            </>
          }
        />
      )}
      {confirmDel && (
        <ConfirmDestructive
          title={t('app.tenants.deleteConfirm.title', { name: tenant.name })}
          confirmKey={tenant.name}
          confirmLabel={t('app.tenants.deleteConfirm.label')}
          busy={busy === 'delete'}
          onCancel={() => setConfirmDel(false)}
          onConfirm={del}
          body={
            <>
              <Trans t={t} i18nKey="app.tenants.deleteConfirm.body" values={{ slug: tenant.slug }} components={{ b: <b /> }} />
            </>
          }
        />
      )}
    </span>
  );
}


function NewTenant({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tier, setTier] = useState('standard');
  const [tenantType, setTenantType] = useState('shared');
  const [region, setRegion] = useState('');
  const [asTrial, setAsTrial] = useState(false);
  const [trialPlan, setTrialPlan] = useState('pro');
  const [trialDays, setTrialDays] = useState(14);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<string[] | null>(null);

  // Residency: when regions are configured, the admin must pick one (it fixes
  // where the customer's database + evidence physically live, permanently).
  const { data: regionInfo } = useQuery({ queryKey: ['regions'], queryFn: systemApi.regions });
  const residency = regionInfo?.enabled ?? false;

  // Auto-derive a slug from the name.
  const onName = (v: string) => {
    setName(v);
    setSlug(v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await systemApi.provision({
        name, slug, tier, tenantType, adminEmail, adminPassword,
        ...(region ? { region } : {}),
        ...(asTrial ? { trialDays, trialPlan } : {}),
      });
      setSteps(res.steps);
      onDone();
    } catch (err: unknown) {
      const r = (err as { response?: { status?: number; data?: { error?: string; message?: string } } })?.response;
      setError(
        r?.status === 409 ? t('app.newTenant.error.slugTaken')
        : r?.data?.message ?? r?.data?.error ?? t('app.newTenant.error.failed'),
      );
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="tk-card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('app.newTenant.title')}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose}><X size={16} /></button>
        </div>
        {steps ? (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm text-ok"><Check size={16} /> {t('app.newTenant.provisioned')}</p>
            <ul className="space-y-1 text-sm">
              {steps.map((s) => <li key={s} className="flex items-center gap-2 text-muted"><Check size={13} className="text-ok" /> {s.replace(/_/g, ' ')}</li>)}
            </ul>
            <button className="tk-btn-primary w-full" onClick={onClose}>{t('common:actions.done')}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="mb-1 block text-sm text-muted">{t('app.newTenant.customerName')}</label>
              <input className="tk-input" value={name} onChange={(e) => onName(e.target.value)} placeholder={t('app.newTenant.customerNamePlaceholder')} autoFocus />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted">{t('app.newTenant.slug')}</label>
              <input className="tk-input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm text-muted">{t('app.newTenant.tier')}</label>
                <select className="tk-input" value={tier} onChange={(e) => setTier(e.target.value)}>
                  {['standard', 'professional', 'enterprise'].map((x) => <option key={x} value={x}>{x.charAt(0).toUpperCase() + x.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm text-muted">{t('app.newTenant.tenantType')}</label>
                <select className="tk-input" value={tenantType} onChange={(e) => setTenantType(e.target.value)}>
                  {['shared', 'dedicated', 'enterprise'].map((x) => <option key={x} value={x}>{x.charAt(0).toUpperCase() + x.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <p className="-mt-1 text-[11px] text-muted">{t('app.newTenant.tierHint')}</p>
            {residency && (
              <div>
                <label className="mb-1 block text-sm text-muted">{t('app.newTenant.region')}</label>
                <select className="tk-input" value={region} onChange={(e) => setRegion(e.target.value)}>
                  <option value="">{t('app.newTenant.selectRegion')}</option>
                  {(regionInfo?.regions ?? []).map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-muted"><Trans t={t} i18nKey="app.newTenant.regionHint" components={{ b: <b /> }} /></p>
              </div>
            )}

            {/* Start-as-trial */}
            <div className="rounded-lg bg-white/5 p-3 ring-1 ring-white/10">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4 accent-[rgb(var(--tk-primary))]" checked={asTrial} onChange={(e) => setAsTrial(e.target.checked)} />
                <span className="font-medium">{t('app.newTenant.startAsTrial')}</span>
              </label>
              {asTrial && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted">{t('app.newTenant.trialPlan')}</label>
                    <select className="tk-input py-1.5 text-sm" value={trialPlan} onChange={(e) => setTrialPlan(e.target.value)}>
                      {['basic', 'pro', 'enterprise'].map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted">{t('app.newTenant.trialLength')}</label>
                    <select className="tk-input py-1.5 text-sm" value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))}>
                      {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{t('app.newTenant.days', { count: d })}</option>)}
                    </select>
                  </div>
                  <p className="col-span-2 text-[11px] text-muted">{t('app.newTenant.trialHint')}</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm text-muted">{t('app.newTenant.adminEmail')}</label>
                <input className="tk-input" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@acme.com" />
              </div>
              <div>
                <label className="mb-1 block text-sm text-muted">{t('app.newTenant.adminPassword')}</label>
                <input className="tk-input" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} />
              </div>
            </div>
            {error && <p className="text-sm text-critical">{error}</p>}
            <p className="text-xs text-muted">{t('app.newTenant.footnote')}</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="tk-btn-ghost" onClick={onClose} disabled={busy}>{t('common:actions.cancel')}</button>
              <button type="submit" className="tk-btn-primary" disabled={busy || !name || !slug || !adminEmail || adminPassword.length < 8 || (residency && !region)}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : t('app.newTenant.provision')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Fleet() {
  const { t } = useTranslation(['admin', 'common']);
  const { data, isLoading } = useQuery({ queryKey: ['fleet'], queryFn: systemApi.fleet });
  // Cross-tenant agents — one system-DB query behind the scenes, refreshed live.
  const { data: agents, isLoading: agentsLoading } = useQuery({
    queryKey: ['fleet-agents'], queryFn: systemApi.agents, refetchInterval: 10000,
  });
  type Row = { id: string; name: string; status: string; devices: number };
  const columns: Column<Row>[] = [
    { key: 'name', header: t('app.fleet.columns.customer'), render: (row) => <span className="font-medium text-fg">{row.name}</span> },
    { key: 'status', header: t('app.fleet.columns.status'), render: (row) => <StatusPill status={row.status} /> },
    { key: 'devices', header: t('app.fleet.columns.devices'), align: 'right', render: (row) => <span className="tk-num text-muted">{row.devices}</span> },
  ];

  const [selectedEdge, setSelectedEdge] = useState<{ edgeId: string; name: string } | null>(null);
  // DataTable keys rows by `id`; FleetAgent keys by edgeId, so alias it.
  type AgentRow = FleetAgent & { id: string };
  const agentRows: AgentRow[] = (agents ?? []).map((a) => ({ ...a, id: a.edgeId }));
  const liveAgents = agentRows.filter((a) => a.status !== 'revoked');
  const onlineAgents = liveAgents.filter((a) => a.connected).length;
  const agentColumns: Column<AgentRow>[] = [
    { key: 'name', header: t('app.fleet.agents.columns.agent'), render: (a) => (
      <div><div className="font-medium text-fg">{a.name}</div><div className="tk-num text-xs text-muted">{a.edgeId}</div></div>
    ) },
    { key: 'tenantName', header: t('app.fleet.agents.columns.customer'), render: (a) => <span className="text-muted">{a.tenantName ?? a.tenantId.slice(0, 8)}</span> },
    { key: 'connected', header: t('app.fleet.agents.columns.status'), render: (a) => (
      a.status === 'revoked'
        ? <span className="text-muted">{t('app.fleet.agents.revoked')}</span>
        : <span className={a.connected ? 'text-ok' : 'text-critical'}>{a.connected ? `● ${t('app.fleet.agents.online')}` : `○ ${t('app.fleet.agents.offline')}`}</span>
    ) },
    { key: 'agentVersion', header: t('app.fleet.agents.columns.version'), render: (a) => (
      <span className="tk-num text-muted">
        {a.agentVersion ?? '—'}
        {a.versionStale && <span className="ml-1 text-warning" title={t('app.fleet.agents.stale')}>⬆</span>}
      </span>
    ) },
    { key: 'lastSeenAt', header: t('app.fleet.agents.columns.lastSeen'), render: (a) => (
      <span className="text-muted">{a.lastSeenAt ? new Date(a.lastSeenAt).toLocaleString() : '—'}</span>
    ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('app.fleet.title')} subtitle={t('app.fleet.subtitle')} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('app.fleet.kpi.customers')} value={data?.tenants ?? 0} tone="info" />
        <KpiStat label={t('app.fleet.kpi.active')} value={data?.activeTenants ?? 0} tone="ok" />
        <KpiStat label={t('app.fleet.kpi.devices')} value={data?.totalDevices ?? 0} tone="info" hint={t('app.fleet.kpi.devicesHint')} />
        <KpiStat label={t('app.fleet.kpi.agents')} value={`${onlineAgents}/${liveAgents.length}`} tone={onlineAgents < liveAgents.length ? 'warning' : 'ok'} hint={t('app.fleet.kpi.agentsHint')} />
      </div>
      <DataTable columns={columns} rows={data?.perTenant} loading={isLoading} emptyIcon={Server} emptyTitle={t('app.fleet.empty')} />
      <div>
        <h3 className="mb-2 text-sm font-semibold text-fg">{t('app.fleet.agents.title')}</h3>
        <DataTable columns={agentColumns} rows={agentRows} loading={agentsLoading} emptyIcon={Server} emptyTitle={t('app.fleet.agents.empty')}
          onRowClick={(a) => setSelectedEdge(selectedEdge?.edgeId === a.edgeId ? null : { edgeId: a.edgeId, name: a.name })} />
        {selectedEdge && <AgentLogsPanel edgeId={selectedEdge.edgeId} name={selectedEdge.name} onClose={() => setSelectedEdge(null)} />}
      </div>
    </div>
  );
}

// Per-edge agent logs — the agent's OWN app-logs (health/errors) shipped up for
// remote observability. Opened by clicking an agent row in Fleet.
function AgentLogsPanel({ edgeId, name, onClose }: { edgeId: string; name: string; onClose: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const { data, isLoading } = useQuery({
    queryKey: ['agent-logs', edgeId], queryFn: () => systemApi.agentLogs(edgeId), refetchInterval: 15000,
  });
  const levelClass = (lvl: string) =>
    lvl === 'error' || lvl === 'fatal' ? 'text-critical'
      : lvl === 'warn' ? 'text-warning'
        : lvl === 'debug' || lvl === 'trace' ? 'text-muted' : 'text-fg';
  return (
    <div className="tk-card mt-3 p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-medium text-fg">
          <ScrollText size={14} className="text-primary" /> {t('app.fleet.agents.logs.title', { name, defaultValue: 'Agent logs — {{name}}' })}
        </h4>
        <button className="tk-btn-ghost px-2 py-1 text-xs" onClick={onClose}><X size={14} /></button>
      </div>
      {isLoading ? <div className="text-xs text-muted">{t('common:loading', 'Loading…')}</div>
        : (data ?? []).length === 0
          ? <div className="text-xs text-muted">{t('app.fleet.agents.logs.empty', 'No agent logs yet. The agent ships warn+ logs every ~60s once it connects.')}</div>
          : (
            <div className="max-h-96 overflow-y-auto rounded-md bg-black/20 p-2 font-mono text-[11px] leading-relaxed">
              {(data ?? []).map((l) => (
                <div key={l.id} className="flex gap-2 border-b border-white/5 py-1 last:border-0">
                  <span className="shrink-0 text-muted">{new Date(l.receivedAt).toLocaleTimeString()}</span>
                  <span className={`shrink-0 uppercase ${levelClass(l.level)}`}>{l.level}</span>
                  {l.module && <span className="shrink-0 text-muted">[{l.module}]</span>}
                  <span className="break-all text-fg">{l.message}</span>
                </div>
              ))}
            </div>
          )}
    </div>
  );
}

type SortKey = 'createdAt' | 'action' | 'actor' | 'tenant';
function Audit() {
  const { t } = useTranslation(['admin', 'common']);
  const [action, setAction] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'createdAt', dir: 'desc' });
  const [details, setDetails] = useState<FilteredAuditEntry | null>(null);

  const filterQ = { action: action || undefined, tenantId: tenantId || undefined, from: from || undefined, to: to ? `${to}T23:59:59` : undefined };
  const { data: rows, isLoading } = useQuery({ queryKey: ['audit-grid', filterQ], queryFn: () => systemApi.auditFiltered(filterQ) });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: systemApi.tenants });

  // Distinct actions for the dropdown (from the current result set).
  const actions = Array.from(new Set((rows ?? []).map((r) => r.action))).sort();
  // Client-side text search + sort.
  const filtered = (rows ?? []).filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [r.action, r.actor, r.tenant, r.resourceType, r.resourceId, r.ip].some((v) => (v ?? '').toLowerCase().includes(s));
  });
  const sorted = [...filtered].sort((a, b) => {
    const av = String(a[sort.key] ?? ''), bv = String(b[sort.key] ?? '');
    return (sort.dir === 'asc' ? 1 : -1) * av.localeCompare(bv);
  });
  const toggleSort = (key: SortKey) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const dl = (format: 'csv' | 'json') => {
    fetch(systemApi.auditExportUrl(format, filterQ), { headers: { Authorization: `Bearer ${getToken() ?? ''}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement('a');
        a.href = url; a.download = `audit-${new Date().toISOString().slice(0, 10)}.${format}`; a.click(); URL.revokeObjectURL(url);
      });
  };
  const clear = () => { setAction(''); setTenantId(''); setFrom(''); setTo(''); setSearch(''); };

  const Th = ({ label, k, className = '' }: { label: string; k?: SortKey; className?: string }) => (
    <th className={`px-4 py-2 font-medium ${k ? 'cursor-pointer select-none hover:text-fg' : ''} ${className}`} onClick={k ? () => toggleSort(k) : undefined}>
      {label}{k && sort.key === k ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );
  const inp = 'tk-input py-1 text-xs';

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('app.audit.title')}
        subtitle={`${t('app.audit.subtitle', { shown: sorted.length, total: (rows ?? []).length })}${(rows ?? []).length >= 5000 ? t('app.audit.capped') : ''}`}
        action={
          <div className="flex gap-2">
            <button className="tk-btn-ghost" onClick={() => dl('csv')}><Download size={15} /> CSV</button>
            <button className="tk-btn-ghost" onClick={() => dl('json')}><Download size={15} /> JSON</button>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="tk-card flex flex-wrap items-end gap-3 p-3">
        <label className="text-xs text-muted">{t('app.audit.filter.action')}<select className={`${inp} mt-1 block w-44`} value={action} onChange={(e) => setAction(e.target.value)}><option value="">{t('app.audit.filter.allActions')}</option>{actions.map((a) => <option key={a} value={a}>{a}</option>)}</select></label>
        <label className="text-xs text-muted">{t('app.audit.filter.customer')}<select className={`${inp} mt-1 block w-44`} value={tenantId} onChange={(e) => setTenantId(e.target.value)}><option value="">{t('app.audit.filter.allCustomers')}</option>{(tenants ?? []).map((tn) => <option key={tn.id} value={tn.id}>{tn.name}</option>)}</select></label>
        <label className="text-xs text-muted">{t('app.audit.filter.from')}<input type="date" className={`${inp} mt-1 block`} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-xs text-muted">{t('app.audit.filter.to')}<input type="date" className={`${inp} mt-1 block`} value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <label className="flex-1 text-xs text-muted">{t('app.audit.filter.search')}<input className={`${inp} mt-1 block w-full`} placeholder={t('app.audit.filter.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} /></label>
        <button className="tk-btn-ghost text-xs" onClick={clear}>{t('app.audit.filter.clear')}</button>
      </div>

      {/* Grid */}
      <div className="tk-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b border-white/10 text-left text-xs text-muted backdrop-blur">
            <tr><Th label={t('app.audit.columns.time')} k="createdAt" /><Th label={t('app.audit.columns.action')} k="action" /><Th label={t('app.audit.columns.actor')} k="actor" /><Th label={t('app.audit.columns.resource')} /><Th label={t('app.audit.columns.customer')} k="tenant" /><Th label="IP" /><Th label="" /></tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={7} className="p-4 text-sm text-muted">{t('app.audit.loading')}</td></tr>
              : sorted.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-sm text-muted">{t('app.audit.empty')}</td></tr>
              : sorted.map((a) => (
                <tr key={a.id} className="border-b border-white/5 last:border-0 hover:bg-white/5">
                  <td className="whitespace-nowrap px-4 py-2 tk-num text-xs text-muted">{new Date(a.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-2"><AuditActionBadge action={a.action} /></td>
                  <td className="px-4 py-2 text-xs">{a.actor ?? <span className="text-muted">{a.actorType ?? '—'}</span>}</td>
                  <td className="px-4 py-2 text-xs text-muted" title={a.resourceId ?? undefined}>{formatResource(a.resourceType, a.resourceId, a.details ?? undefined)}</td>
                  <td className="px-4 py-2 text-xs text-muted">{a.tenant ?? '—'}</td>
                  <td className="px-4 py-2 tk-num text-xs text-muted">{a.ip ?? '—'}</td>
                  <td className="px-4 py-2 text-right">{a.details && Object.keys(a.details).length > 0 && <button className="tk-btn-ghost px-1.5 py-0.5 text-xs" onClick={() => setDetails(a)}>{t('app.audit.details')}</button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {details && (
        <div className="tk-modal-overlay" onClick={() => setDetails(null)}>
          <div className="tk-modal w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between"><div className="font-semibold">{details.action}</div><button className="tk-btn-ghost px-2" onClick={() => setDetails(null)}><X size={16} /></button></div>
            <div className="space-y-1 text-xs text-muted">
              <div>{new Date(details.createdAt).toLocaleString()} · {details.actor ?? details.actorType} · {details.ip}</div>
              {details.tenant && <div>{t('app.audit.detailsModal.customer', { name: details.tenant })}</div>}
            </div>
            <pre className="mt-3 max-h-80 overflow-auto rounded-md bg-black/30 p-3 text-xs text-fg">{JSON.stringify(details.details, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Billing revenue: MRR + per-customer revenue, from the overview rollup ────
// Surfaces data the platform already computes (overview.mrrCents + perTenant
// priceCents) but never showed on the billing screen.
function BillingRevenue() {
  const { t } = useTranslation(['admin', 'common']);
  const { data } = useQuery({ queryKey: ['overview'], queryFn: systemApi.overview });
  const money = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const rows = (data?.perTenant ?? []).filter((r) => r.priceCents > 0).sort((a, b) => b.priceCents - a.priceCents);
  const mrr = data?.mrrCents ?? 0;
  const paying = rows.length;
  const arpa = paying ? Math.round(mrr / paying) : 0;
  const overLimit = data?.overLimitTenants ?? 0;
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('app.billing.kpi.mrr', 'MRR')} value={money(mrr)} tone="ok" hint={t('app.billing.kpi.mrrHint', 'monthly recurring')} />
        <KpiStat label={t('app.billing.kpi.arr', 'ARR')} value={money(mrr * 12)} tone="info" hint={t('app.billing.kpi.arrHint', 'annualized')} />
        <KpiStat label={t('app.billing.kpi.paying', 'Paying customers')} value={paying} tone="info" />
        <KpiStat label={t('app.billing.kpi.arpa', 'ARPA')} value={money(arpa)} tone="info" hint={t('app.billing.kpi.arpaHint', 'avg / customer')} />
      </div>

      {/* Over-limit callout — customers exceeding their plan (upsell / overage). */}
      {overLimit > 0 && (
        <div className="tk-card flex items-center gap-2 border-warning/30 bg-warning/10 p-3 text-sm text-warning">
          <span>⚠</span>
          <span>{t('app.billing.overLimit', { count: overLimit, defaultValue: '{{count}} customer(s) are over their plan limits — candidates for an upgrade or overage charge.' })}</span>
        </div>
      )}

      <div className="tk-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted">
            <tr className="border-b border-white/10">
              <th className="w-6 px-2 py-3" />
              <th className="px-4 py-3">{t('app.billing.revenue.customer', 'Customer')}</th>
              <th className="px-4 py-3">{t('app.billing.revenue.plan', 'Plan')}</th>
              <th className="px-4 py-3">{t('app.billing.revenue.subStatus', 'Subscription')}</th>
              <th className="px-4 py-3 text-right">{t('app.billing.revenue.mrr', 'MRR')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted">{t('app.billing.revenue.empty', 'No paying customers yet')}</td></tr>}
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className="cursor-pointer border-b border-white/5 last:border-0 hover:bg-white/[0.02]" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                  <td className="px-2 py-2.5 text-center text-muted">{expanded === r.id ? '▾' : '▸'}</td>
                  <td className="px-4 py-2.5"><span className="font-medium text-fg">{r.name}</span>{r.over && <span className="ml-2 text-xs text-warning" title={t('app.billing.revenue.overLimit', 'Over plan limit')}>⚠</span>}</td>
                  <td className="px-4 py-2.5 text-muted">{r.plan ?? '—'}</td>
                  <td className="px-4 py-2.5"><span className={r.subStatus === 'active' ? 'text-ok' : 'text-muted'}>{r.subStatus ?? '—'}</span></td>
                  <td className="px-4 py-2.5 text-right tk-num">{money(r.priceCents)}</td>
                </tr>
                {expanded === r.id && (
                  <tr className="border-b border-white/5 bg-white/[0.02]">
                    <td />
                    <td colSpan={4} className="px-4 py-3"><TenantInvoices tenantId={r.id} tenantName={r.name} /></td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Per-customer billing controls: assign a plan from the catalog, capture a card
// (opens the GreenInvoice form URL, sink-safe), and charge now. Card status shows
// last4 or "no card". All actions go through the super-admin system routes.
function BillingCustomerControls({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: detail } = useQuery({ queryKey: ['tenant', tenantId], queryFn: () => systemApi.tenantDetail(tenantId) });
  const { data: plans } = useQuery({ queryKey: ['plans'], queryFn: systemApi.plans });
  const [busy, setBusy] = useState<'plan' | 'card' | 'charge' | null>(null);
  const [tokenInput, setTokenInput] = useState('');
  const [showToken, setShowToken] = useState(false);
  const sub = detail?.subscription ?? null;

  const assignPlan = async (planId: string) => {
    setBusy('plan');
    try { await systemApi.setSubscription(tenantId, { planId: planId || null }); qc.invalidateQueries({ queryKey: ['tenant', tenantId] }); qc.invalidateQueries({ queryKey: ['overview'] }); toast.ok(t('app.billing.customer.planAssigned', 'Plan updated')); }
    catch { toast.error(t('app.billing.customer.planFailed', 'Could not update plan')); }
    finally { setBusy(null); }
  };
  const captureCard = async () => {
    setBusy('card');
    try {
      const r = await systemApi.paymentForm(tenantId);
      if (r.sink) { toast.ok(t('app.billing.customer.formSink', 'Sink mode — GreenInvoice not configured, no real form. Paste a token below to simulate.')); setShowToken(true); }
      else if (r.url) { window.open(r.url, '_blank', 'noopener'); toast.ok(t('app.billing.customer.formOpened', 'Card-capture form opened')); }
    } catch { toast.error(t('app.billing.customer.formFailed', 'Could not open card form')); }
    finally { setBusy(null); }
  };
  const saveToken = async () => {
    if (!tokenInput.trim()) return;
    setBusy('card');
    try { await systemApi.setCardToken(tenantId, { cardTokenId: tokenInput.trim(), cardLast4: tokenInput.trim().slice(-4) }); qc.invalidateQueries({ queryKey: ['tenant', tenantId] }); setTokenInput(''); setShowToken(false); toast.ok(t('app.billing.customer.cardSaved', 'Card token saved')); }
    catch { toast.error(t('app.billing.customer.cardFailed', 'Could not save token')); }
    finally { setBusy(null); }
  };
  const chargeNow = async () => {
    setBusy('charge');
    try {
      const r = await systemApi.chargeNow(tenantId);
      qc.invalidateQueries({ queryKey: ['invoices', tenantId] }); qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      toast.ok(r.sink
        ? t('app.billing.customer.chargedSink', { amount: `$${((r.amountCents ?? 0) / 100).toLocaleString()}`, defaultValue: 'Charged {{amount}} (sink mode — no real payment)' })
        : t('app.billing.customer.charged', { amount: `$${((r.amountCents ?? 0) / 100).toLocaleString()}`, defaultValue: 'Charged {{amount}}' }));
    } catch { toast.error(t('app.billing.customer.chargeFailed', 'Charge failed')); }
    finally { setBusy(null); }
  };

  const activePlans = (plans ?? []).filter((p) => p.isActive);
  return (
    <div className="tk-card mb-3 p-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs text-muted">{t('app.billing.customer.plan', 'Plan')}
          <select className="tk-input mt-1 w-full py-1.5 text-sm" value={sub?.planId ?? ''} disabled={busy === 'plan'} onChange={(e) => assignPlan(e.target.value)}>
            <option value="">{t('app.billing.customer.noPlan', '— no plan (flat price) —')}</option>
            {activePlans.map((p) => <option key={p.id} value={p.id}>{p.name} · {money(p.basePriceCents)}{p.perCameraCents ? `+${money(p.perCameraCents)}/cam` : ''}</option>)}
          </select>
        </label>
        <div className="text-xs text-muted">{t('app.billing.customer.card', 'Card on file')}
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm text-fg">{sub?.cardLast4 ? `•••• ${sub.cardLast4}` : sub?.cardTokenId ? t('app.billing.customer.cardYes', 'saved') : t('app.billing.customer.cardNo', 'no card')}</span>
            <button className="tk-btn-ghost text-xs" disabled={busy === 'card'} onClick={captureCard}><CreditCard size={13} /> {t('app.billing.customer.capture', 'Capture card')}</button>
          </div>
        </div>
        <div className="text-xs text-muted">{t('app.billing.customer.actions', 'Actions')}
          <div className="mt-1">
            <button className="tk-btn-primary text-xs" disabled={busy === 'charge' || (!sub?.cardTokenId)} title={!sub?.cardTokenId ? t('app.billing.customer.needCard', 'Capture a card first') : undefined} onClick={chargeNow}>
              {busy === 'charge' ? t('app.billing.customer.charging', 'Charging…') : t('app.billing.customer.chargeNow', 'Charge now')}
            </button>
            {sub?.lastChargeStatus && <span className="ml-2 text-[11px] text-muted">{t('app.billing.customer.last', 'last')}: {sub.lastChargeStatus}</span>}
          </div>
        </div>
      </div>
      {showToken && (
        <div className="mt-2 flex items-center gap-2">
          <input className="tk-input flex-1 py-1.5 text-sm" placeholder={t('app.billing.customer.tokenPlaceholder', 'Paste GreenInvoice card token id')} value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} />
          <button className="tk-btn-ghost text-xs" disabled={busy === 'card' || !tokenInput.trim()} onClick={saveToken}>{t('common:actions.save', 'Save')}</button>
        </div>
      )}
    </div>
  );
}

// Per-customer invoice history + generate action. Lazy-fetched when a revenue
// row is expanded. Reuses the existing per-tenant invoice endpoints (no new API).
function TenantInvoices({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const { data: invoices, isLoading } = useQuery({
    queryKey: ['invoices', tenantId], queryFn: () => systemApi.invoices(tenantId),
  });
  const money = (cents: number | null) => (cents == null ? '—' : `$${(cents / 100).toLocaleString()}`);
  const generate = async () => {
    setBusy(true);
    try {
      const r = await systemApi.generateInvoice(tenantId);
      toast.ok(r.sink
        ? t('app.billing.invoices.generatedSink', 'Invoice recorded (sink mode — provider not configured)')
        : t('app.billing.invoices.generated', 'Invoice generated'));
      qc.invalidateQueries({ queryKey: ['invoices', tenantId] });
    } catch { toast.error(t('app.billing.invoices.generateFailed', 'Could not generate invoice')); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <BillingCustomerControls tenantId={tenantId} />
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-medium text-muted">{t('app.billing.invoices.title', { name: tenantName, defaultValue: 'Invoices — {{name}}' })}</div>
        <button className="tk-btn-ghost text-xs" disabled={busy} onClick={generate}>
          {busy ? t('app.billing.invoices.generating', 'Generating…') : t('app.billing.invoices.generate', 'Generate invoice')}
        </button>
      </div>
      {isLoading ? <div className="text-xs text-muted">{t('common:loading', 'Loading…')}</div>
        : (invoices ?? []).length === 0 ? <div className="text-xs text-muted">{t('app.billing.invoices.empty', 'No invoices yet')}</div>
          : (
            <table className="w-full text-xs">
              <tbody>
                {(invoices ?? []).map((inv) => (
                  <tr key={inv.id} className="border-b border-white/5 last:border-0">
                    <td className="py-1.5 tk-num text-muted">{inv.number ?? inv.id.slice(0, 8)}</td>
                    <td className="py-1.5 text-muted">{new Date(inv.periodStart).toLocaleDateString()} – {new Date(inv.periodEnd).toLocaleDateString()}</td>
                    <td className="py-1.5"><span className={inv.status === 'paid' ? 'text-ok' : inv.status === 'overdue' ? 'text-critical' : 'text-muted'}>{inv.status}</span></td>
                    <td className="py-1.5 text-right tk-num">{money(inv.amountCents)}</td>
                    <td className="py-1.5 text-right">{inv.externalUrl && <a href={inv.externalUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">{t('app.billing.invoices.view', 'View')}</a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </div>
  );
}

// Top-level Billing page — the revenue view (KPIs + per-customer revenue).
function BillingPage() {
  const { t } = useTranslation(['admin', 'common']);
  return (
    <div className="space-y-8">
      <PageHeader title={t('app.billing.title')} subtitle={t('app.billing.revenueSubtitle', 'Recurring revenue and per-customer billing across the platform.')} />
      <BillingRevenue />
      <PlansEditor />
    </div>
  );
}

// Plan/pricing catalog editor. Model-agnostic: a plan = base + optional per-unit
// rates + included quantity, so the same form expresses tier, metered, or bundle.
const money = (cents: number | null | undefined) => (cents == null ? '—' : `$${(cents / 100).toLocaleString()}`);
function PlansEditor() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: plans } = useQuery({ queryKey: ['plans'], queryFn: systemApi.plans });
  const [editing, setEditing] = useState<Partial<Plan> | null>(null);

  const blank: Partial<Plan> = { code: '', name: '', currency: 'USD', billingInterval: 'monthly', basePriceCents: 0, includedCameras: null, perCameraCents: null, includedDevices: null, perDeviceCents: null };
  const save = async (p: Partial<Plan>) => {
    try {
      if (p.id) await systemApi.updatePlan(p.id, p); else await systemApi.createPlan(p);
      qc.invalidateQueries({ queryKey: ['plans'] }); setEditing(null); toast.ok(t('app.plans.saved', 'Plan saved'));
    } catch { toast.error(t('app.plans.saveFailed', 'Could not save plan')); }
  };
  const remove = async (id: string) => {
    try { await systemApi.deletePlan(id); qc.invalidateQueries({ queryKey: ['plans'] }); toast.ok(t('app.plans.removed', 'Plan removed')); }
    catch { toast.error(t('app.plans.removeFailed', 'Could not remove plan')); }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-fg">{t('app.plans.title', 'Plans & pricing')}</h3>
        <button className="tk-btn-ghost text-xs" onClick={() => setEditing(blank)}><Plus size={14} /> {t('app.plans.add', 'Add plan')}</button>
      </div>
      <p className="mb-3 text-xs text-muted">{t('app.plans.hint', 'A plan is a base price plus optional per-camera / per-device rates and included quantities — so it works as a flat tier, per-camera metering, or a bundle.')}</p>
      <div className="tk-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted">
            <tr className="border-b border-white/10">
              <th className="px-4 py-3">{t('app.plans.name', 'Plan')}</th>
              <th className="px-4 py-3 text-right">{t('app.plans.base', 'Base')}</th>
              <th className="px-4 py-3 text-right">{t('app.plans.perCamera', 'Per camera')}</th>
              <th className="px-4 py-3 text-right">{t('app.plans.included', 'Included cameras')}</th>
              <th className="px-4 py-3">{t('app.plans.interval', 'Interval')}</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {(plans ?? []).length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted">{t('app.plans.empty', 'No plans yet — add one to start pricing customers')}</td></tr>}
            {(plans ?? []).filter((p) => p.isActive).map((p) => (
              <tr key={p.id} className="border-b border-white/5 last:border-0">
                <td className="px-4 py-2.5"><span className="font-medium text-fg">{p.name}</span> <span className="tk-num text-xs text-muted">{p.code}</span></td>
                <td className="px-4 py-2.5 text-right tk-num">{money(p.basePriceCents)}</td>
                <td className="px-4 py-2.5 text-right tk-num text-muted">{p.perCameraCents ? money(p.perCameraCents) : '—'}</td>
                <td className="px-4 py-2.5 text-right tk-num text-muted">{p.includedCameras ?? '—'}</td>
                <td className="px-4 py-2.5 text-muted">{p.billingInterval}</td>
                <td className="px-4 py-2.5 text-right">
                  <button className="tk-btn-ghost px-1.5 py-1" title={t('common:actions.edit', 'Edit')} onClick={() => setEditing(p)}><Pencil size={13} /></button>
                  <button className="tk-btn-ghost px-1.5 py-1" title={t('common:actions.remove', 'Remove')} onClick={() => remove(p.id)}><X size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PlanForm plan={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function PlanForm({ plan, onClose, onSave }: { plan: Partial<Plan>; onClose: () => void; onSave: (p: Partial<Plan>) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [p, setP] = useState<Partial<Plan>>(plan);
  const dollars = (cents: number | null | undefined) => (cents == null ? '' : String(cents / 100));
  const toCents = (v: string) => (v === '' ? null : Math.round(Number(v) * 100));
  const set = (k: keyof Plan, v: unknown) => setP((cur) => ({ ...cur, [k]: v }));
  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <form className="tk-modal w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); onSave(p); }}>
        <div className="mb-3 text-base font-semibold">{p.id ? t('app.plans.edit', 'Edit plan') : t('app.plans.add', 'Add plan')}</div>
        <div className="grid grid-cols-2 gap-3 text-xs text-muted">
          <label>{t('app.plans.name', 'Name')}<input required className="tk-input mt-1 w-full py-1.5 text-sm" value={p.name ?? ''} onChange={(e) => set('name', e.target.value)} /></label>
          <label>{t('app.plans.code', 'Code')}<input required className="tk-input mt-1 w-full py-1.5 text-sm" value={p.code ?? ''} onChange={(e) => set('code', e.target.value)} /></label>
          <label>{t('app.plans.base', 'Base price')} ($)<input type="number" min={0} step="0.01" className="tk-input mt-1 w-full py-1.5 text-sm" value={dollars(p.basePriceCents)} onChange={(e) => set('basePriceCents', toCents(e.target.value) ?? 0)} /></label>
          <label>{t('app.plans.interval', 'Interval')}<select className="tk-input mt-1 w-full py-1.5 text-sm" value={p.billingInterval ?? 'monthly'} onChange={(e) => set('billingInterval', e.target.value)}><option value="monthly">monthly</option><option value="yearly">yearly</option></select></label>
          <label>{t('app.plans.included', 'Included cameras')}<input type="number" min={0} className="tk-input mt-1 w-full py-1.5 text-sm" value={p.includedCameras ?? ''} onChange={(e) => set('includedCameras', e.target.value === '' ? null : Number(e.target.value))} placeholder={t('app.plans.unlimited', 'unlimited')} /></label>
          <label>{t('app.plans.perCamera', 'Per extra camera')} ($)<input type="number" min={0} step="0.01" className="tk-input mt-1 w-full py-1.5 text-sm" value={dollars(p.perCameraCents)} onChange={(e) => set('perCameraCents', toCents(e.target.value))} placeholder={t('app.plans.none', 'none')} /></label>
        </div>
        <p className="mt-2 text-[11px] text-muted">{t('app.plans.formHint', 'Leave per-camera blank for a flat tier. Set it (with or without an included count) for metered or bundle pricing.')}</p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="tk-btn-ghost" onClick={onClose}>{t('common:actions.cancel', 'Cancel')}</button>
          <button type="submit" className="tk-btn-primary">{t('common:actions.save', 'Save')}</button>
        </div>
      </form>
    </div>
  );
}

// ── Billing: global GreenInvoice config (write-only secrets) + Test ──────────
// `embedded` drops the page header so it can render inside the Settings tab.
function Billing({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const { data: cfg } = useQuery({ queryKey: ['billing-config'], queryFn: systemApi.billingConfig });
  const [apiId, setApiId] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const save = async () => {
    setBusy('save'); setMsg(null);
    try {
      await systemApi.saveBillingConfig({ apiId: apiId || undefined, apiSecret: apiSecret || undefined, baseUrl: baseUrl || undefined, isEnabled: true });
      setApiId(''); setApiSecret(''); queryClient.invalidateQueries({ queryKey: ['billing-config'] });
      setMsg({ tone: 'ok', text: t('app.billing.msg.saved') });
    } catch { setMsg({ tone: 'err', text: t('app.billing.msg.saveFailed') }); }
    finally { setBusy(null); }
  };
  const test = async () => {
    setBusy('test'); setMsg(null);
    try {
      const r = await systemApi.testBilling();
      // Not-configured is its own outcome (CFG-04): the API used to return a
      // green ok:true for it, so the UI reported "connected" when nothing was
      // wired up. It's now ok:false+notConfigured — surfaced as the (already
      // accurate) sink-mode warning, NOT as a connection failure.
      if (r.notConfigured) setMsg({ tone: 'warn', text: t('app.billing.msg.sinkMode') });
      else if (!r.ok) setMsg({ tone: 'err', text: t('app.billing.msg.connectionFailed', { error: r.error ?? t('app.billing.msg.unknownError') }) });
      else if (r.sink) setMsg({ tone: 'warn', text: t('app.billing.msg.sinkMode') });
      else setMsg({ tone: 'ok', text: t('app.billing.msg.connected') });
    } catch { setMsg({ tone: 'err', text: t('app.billing.msg.testFailed') }); }
    finally { setBusy(null); }
  };
  const toneClass = (tone: 'ok' | 'warn' | 'err') => tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warning' : 'text-critical';
  return (
    <div className="space-y-6">
      {!embedded && <PageHeader title={t('app.billing.title')} subtitle={t('app.billing.subtitle')} />}
      {/* Revenue view (MRR/ARR + per-customer) lives on the top-level Billing
          page now; this component is only the invoicing-PROVIDER credentials. */}
      <div className="tk-card max-w-lg p-4">
        <div className="mb-2 text-sm font-medium">{t('app.billing.providerTitle', 'Invoicing provider')}</div>
        <p className="mb-3 text-sm text-muted"><Trans t={t} i18nKey="app.billing.intro" components={{ b: <b className={cfg?.configured ? 'text-ok' : 'text-warning'} /> }} values={{ status: cfg?.configured ? t('app.billing.status.configured') : t('app.billing.status.notConfigured') }} /></p>
        <div className="space-y-3 text-xs text-muted">
          <label className="block">{t('app.billing.apiId')}<input className="tk-input mt-1 w-full py-1.5 text-sm" value={apiId} onChange={(e) => setApiId(e.target.value)} placeholder={cfg?.configured ? t('app.billing.leaveBlank') : t('app.billing.apiIdPlaceholder')} /></label>
          <label className="block">{t('app.billing.apiSecret')}<input className="tk-input mt-1 w-full py-1.5 text-sm" type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} placeholder={cfg?.configured ? t('app.billing.leaveBlank') : t('app.billing.apiSecretPlaceholder')} /></label>
          <label className="block">{t('app.billing.baseUrl')}<input className="tk-input mt-1 w-full py-1.5 text-sm" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.greeninvoice.co.il/api/v1" /></label>
        </div>
        {msg && <p className={`mt-3 text-sm ${toneClass(msg.tone)}`}>{msg.text}</p>}
        <div className="mt-4 flex gap-2">
          <button className="tk-btn-primary" disabled={!!busy} onClick={save}>{busy === 'save' ? t('app.billing.saving') : t('common:actions.save')}</button>
          <button className="tk-btn-ghost" disabled={!!busy} onClick={test}>{busy === 'test' ? t('app.billing.testing') : t('app.billing.testConnection')}</button>
        </div>
      </div>
      <p className="text-xs text-muted">{t('app.billing.footnote')}</p>
    </div>
  );
}

// ── Services: deep health of the platform's own services ─────────────────────
function Services() {
  const { t } = useTranslation(['admin', 'common']);
  const { data, isLoading } = useQuery({ queryKey: ['health-deep'], queryFn: systemApi.healthDeep, refetchInterval: 10000 });
  // Color/label map: ok=green, down=red (a real outage of a required service),
  // not_deployed / not_configured = grey (optional service simply not running —
  // not an error), unknown=amber.
  const SVC = {
    ok: { dot: 'text-ok', label: t('app.services.status.up') },
    down: { dot: 'text-critical', label: t('app.services.status.down') },
    not_deployed: { dot: 'text-muted', label: t('app.services.status.notDeployed') },
    not_configured: { dot: 'text-muted', label: t('app.services.status.notConfigured') },
    unknown: { dot: 'text-warning', label: t('app.services.status.unknown') },
  } as const;
  const meta = (s: string) => SVC[s as keyof typeof SVC] ?? SVC.unknown;
  // Friendly display name for the service key.
  const name = (k: string) => k.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div className="space-y-6">
      <PageHeader title={t('app.services.title')} subtitle={t('app.services.subtitle')} />
      {isLoading ? <div className="text-sm text-muted">{t('app.services.loading')}</div> : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          {(data ?? []).map((s) => {
            const m = meta(s.status);
            return (
              <div key={s.service} className="tk-card p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{name(s.service)}{s.required === false && <span className="ms-1.5 rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-normal text-muted ring-1 ring-white/10">{t('app.services.optional')}</span>}</span>
                  <span className={`flex shrink-0 items-center gap-1 text-sm ${m.dot}`}>● {m.label}</span>
                </div>
                <div className="mt-1 text-xs text-muted">{s.detail}</div>
              </div>
            );
          })}
        </div>
      )}
      <MetricsTrends />
      <GrafanaPanel />
    </div>
  );
}

// Native platform time-series — the built-in trend charts (fleet, edges, tenants,
// MRR) sampled every 15 min into metrics_samples. Replaces the external-Grafana
// embed as the headline monitoring on the Services page; no extra infra.
const RANGES = ['24h', '7d', '30d'] as const;
type MetricsRange = (typeof RANGES)[number];
function MetricsTrends() {
  const { t } = useTranslation(['admin', 'common']);
  const [range, setRange] = useState<MetricsRange>('24h');
  const { data } = useQuery({ queryKey: ['metrics-history', range], queryFn: () => systemApi.metricsHistory(range), refetchInterval: 60_000 });
  const points = data?.points ?? [];

  // Label formatting: time-of-day for 24h, short date otherwise.
  const label = (iso: string) => {
    const d = new Date(iso);
    return range === '24h'
      ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  const series = (key: keyof MetricsPoint) => points.map((p) => ({ label: label(p.t), value: Number(p[key]) }));
  const money = (v: number) => (v >= 100000 ? `$${(v / 100000).toFixed(1)}k` : `$${Math.round(v / 100)}`);

  const charts: Array<{ key: keyof MetricsPoint; title: string; color: string; fmt?: (v: number) => string }> = [
    { key: 'totalDevices', title: t('app.metrics.devices', 'Devices'), color: CHART.primary },
    { key: 'totalCameras', title: t('app.metrics.cameras', 'Cameras'), color: CHART.ok },
    { key: 'edgesConnected', title: t('app.metrics.edges', 'Edges connected'), color: CHART.warning },
    { key: 'activeTenants', title: t('app.metrics.activeTenants', 'Active tenants'), color: CHART.primary },
    { key: 'mrrCents', title: t('app.metrics.mrr', 'MRR'), color: CHART.ok, fmt: money },
  ];

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-fg"><LineChartIcon size={15} className="text-primary" /> {t('app.metrics.title', 'Platform trends')}</h3>
        <div className="flex items-center gap-1 rounded-md bg-white/5 p-0.5 ring-1 ring-white/10">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setRange(r)}
              className={`rounded px-2 py-1 text-xs ${range === r ? 'bg-primary text-white' : 'text-muted hover:text-fg'}`}>{r}</button>
          ))}
        </div>
      </div>
      <Suspense fallback={<div className="grid gap-4 lg:grid-cols-2"><div className="tk-card h-56 animate-pulse" /><div className="tk-card h-56 animate-pulse" /></div>}>
        <div className="grid gap-4 lg:grid-cols-2">
          {charts.map((c) => (
            <ChartCard key={c.key} title={c.title}>
              <TrendArea data={series(c.key)} color={c.color} valueFmt={c.fmt} />
            </ChartCard>
          ))}
        </div>
      </Suspense>
      {points.length === 0 && (
        <p className="mt-2 text-xs text-muted">{t('app.metrics.collecting', 'Collecting — the first sample lands within 15 minutes of the worker starting.')}</p>
      )}
    </div>
  );
}

// Resolve the Grafana dashboard URL from either a build-time env var
// (VITE_GRAFANA_URL) or a runtime-injected global (window.__IOSENTRA_CONFIG__.
// grafanaUrl, set by a tiny /config.js the deployment serves) — so a deployed
// admin can be pointed at Grafana WITHOUT a rebuild. localStorage gives an
// operator a no-deploy override too. The setup card below writes that override.
function resolveGrafanaUrl(): string {
  const runtime = (window as unknown as { __IOSENTRA_CONFIG__?: { grafanaUrl?: string } }).__IOSENTRA_CONFIG__?.grafanaUrl;
  const stored = localStorage.getItem('iosentra_grafana_url') || undefined;
  const env = import.meta.env.VITE_GRAFANA_URL as string | undefined;
  return (stored || runtime || env || '').trim();
}

// Embeds a Grafana dashboard when a URL is configured (env / runtime / operator
// override); otherwise a usable setup card that lets the operator paste a URL
// and embed it on the spot. (Grafana is the external time-series layer; the
// tiles above are the in-app deep health.)
function GrafanaPanel() {
  const { t } = useTranslation(['admin', 'common']);
  const [url, setUrl] = useState(resolveGrafanaUrl());
  const [draft, setDraft] = useState('');
  const toast = useToast();

  const apply = () => {
    const v = draft.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) { toast.warn(t('app.grafana.toast.invalidUrl')); return; }
    localStorage.setItem('iosentra_grafana_url', v);
    setUrl(v);
    toast.ok(t('app.grafana.toast.embedded'));
  };
  // Drop the operator override, then fall back to any env/runtime default.
  const clear = () => { localStorage.removeItem('iosentra_grafana_url'); setDraft(''); setUrl(resolveGrafanaUrl()); };

  if (url) {
    return (
      <div className="tk-card overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
          <span className="flex items-center gap-2 text-sm font-medium"><Activity size={15} className="text-primary" /> {t('app.grafana.heading')}</span>
          <div className="flex items-center gap-3">
            <button onClick={clear} className="text-xs text-muted hover:text-fg" title={t('app.grafana.forgetUrl')}>{t('app.grafana.change')}</button>
            <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">{t('app.grafana.openInGrafana')} <ExternalLink size={12} /></a>
          </div>
        </div>
        <iframe title="Grafana" src={url} className="h-[600px] w-full border-0" />
      </div>
    );
  }
  // No URL configured: the native trend charts above are the built-in monitoring,
  // so the external-Grafana embed is demoted to an optional, collapsed extra for
  // operators who run their own Grafana.
  return (
    <details className="tk-card p-4">
      <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-muted">
        <Activity size={15} /> {t('app.grafana.advancedHeading', 'Advanced: external Grafana (optional)')}
      </summary>
      <p className="mt-2 text-xs text-muted">
        {t('app.grafana.optionalHint', 'The trend charts above are the built-in monitoring — no extra infrastructure needed. If you run your own Grafana, paste a kiosk-mode dashboard URL to embed it here too.')}
      </p>
      <div className="mt-3 flex gap-2">
        <input
          className="tk-input flex-1 py-1.5 text-sm"
          placeholder={t('app.grafana.urlPlaceholder', 'Grafana dashboard URL (kiosk mode)')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
        />
        <button className="tk-btn-primary px-3 py-1.5 text-sm" onClick={apply} disabled={!draft.trim()}>{t('app.grafana.embed')}</button>
      </div>
    </details>
  );
}

// ── Settings: tabbed — Account (+ 2FA), and Billing (moved from top nav) ──────
function SettingsView({ user }: { user: SystemUser }) {
  const { t } = useTranslation(['admin', 'common']);
  const [tab, setTab] = useState<'account' | 'billing' | 'messaging'>('account');
  const TABS = [
    { key: 'account' as const, label: t('app.settings.tabs.account', 'Account & security') },
    { key: 'billing' as const, label: t('app.settings.tabs.billing', 'Billing') },
    { key: 'messaging' as const, label: t('app.settings.tabs.messaging', 'Messaging') },
  ];
  return (
    <div className="space-y-4">
      <PageHeader title={t('app.settings.title')} subtitle={t('app.settings.subtitle')} />
      <div className="flex gap-1 border-b border-white/10">
        {TABS.map((x) => (
          <button key={x.key} onClick={() => setTab(x.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === x.key ? 'border-primary text-fg' : 'border-transparent text-muted hover:text-fg'}`}>
            {x.label}
          </button>
        ))}
      </div>

      {tab === 'account' && (
        <div className="space-y-6">
          <div className="tk-card max-w-2xl p-4">
            <div className="text-sm font-medium">{t('app.settings.account')}</div>
            <dl className="mt-2 space-y-1 text-sm text-muted">
              <div className="flex justify-between"><dt>{t('app.settings.email')}</dt><dd className="text-fg">{user.email}</dd></div>
              <div className="flex justify-between"><dt>{t('app.settings.roles')}</dt><dd className="text-fg">{user.roles.join(', ')}</dd></div>
            </dl>
          </div>
          <SecurityPanel />
        </div>
      )}

      {tab === 'billing' && <Billing embedded />}

      {tab === 'messaging' && <MessagingPanel embedded />}
    </div>
  );
}

// Platform-global messaging — super-admins configure channel CREDENTIALS +
// message TEMPLATES here; every tenant shares them (recipients stay per-tenant in
// the operator app). Credentials are encrypted server-side and never echoed.
type FieldDef = { key: string; label: string; secret?: boolean };

// Email has multiple backends selected by config.providerType; each shows its
// own fields. `fromEmail` is always present. The backend connector branches on
// the same providerType value.
const EMAIL_PROVIDER_TYPES = ['smtp', 'sendgrid', 'm365-graph', 'm365'] as const;
const EMAIL_TYPE_LABELS: Record<string, string> = {
  smtp: 'Generic SMTP',
  sendgrid: 'SendGrid',
  'm365-graph': 'Microsoft 365 (Graph API)',
  m365: 'Microsoft 365 (SMTP)',
};
const EMAIL_TYPE_FIELDS: Record<string, FieldDef[]> = {
  smtp: [
    { key: 'host', label: 'SMTP host' }, { key: 'port', label: 'Port' },
    { key: 'username', label: 'Username' }, { key: 'password', label: 'Password', secret: true },
  ],
  // Graph API (recommended): OAuth2 client-credentials, app scoped to Mail.Send.
  // The Azure app needs Mail.Send (Application) + admin consent. `fromEmail` (the
  // sending mailbox) is appended for every email type by the card.
  'm365-graph': [
    { key: 'tenantId', label: 'Directory (tenant) ID' },
    { key: 'clientId', label: 'Application (client) ID' },
    { key: 'clientSecret', label: 'Client secret', secret: true },
  ],
  m365: [
    // Host/port default to smtp.office365.com:587 server-side; only auth needed.
    { key: 'username', label: 'Mailbox / user' }, { key: 'password', label: 'Password / app password', secret: true },
  ],
  sendgrid: [
    { key: 'apiKey', label: 'API key', secret: true },
  ],
};

const CHANNEL_FIELDS: Record<string, FieldDef[]> = {
  whatsapp: [
    { key: 'accessToken', label: 'Access token', secret: true }, { key: 'phoneNumberId', label: 'Phone number ID' },
  ],
  telegram: [
    { key: 'botToken', label: 'Bot token', secret: true },
  ],
  slack: [{ key: 'webhookUrl', label: 'Incoming webhook URL', secret: true }],
  webhook: [{ key: 'url', label: 'Webhook URL', secret: true }],
};
// Channels a super-admin can configure globally. Once enabled here, each tenant
// sets its own recipient targets (webhook URL / Slack channel) in the app's
// Settings → Recipients tab. Field defs for all of these exist above.
const UI_CHANNELS = ['email', 'whatsapp', 'telegram', 'slack', 'webhook'];
// Per-channel template fields (what the editor lets you author).
const CHANNEL_TEMPLATE_FIELDS: Record<string, { key: string; label: string; multiline?: boolean }[]> = {
  email: [{ key: 'subject', label: 'Subject' }, { key: 'body', label: 'Body', multiline: true }],
  whatsapp: [{ key: 'text', label: 'Message', multiline: true }],
  telegram: [{ key: 'text', label: 'Message', multiline: true }],
  slack: [{ key: 'text', label: 'Message', multiline: true }],
  webhook: [{ key: 'text', label: 'Message', multiline: true }],
};

function MessagingPanel({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const toast = useToast();
  const provs = useQuery({ queryKey: ['msg-providers'], queryFn: systemApi.messagingProviders });
  const [tab, setTab] = useState<'providers' | 'templates'>('providers');

  return (
    <div className="max-w-3xl space-y-4">
      {!embedded && <PageHeader title={t('app.messaging.title', 'Messaging')} subtitle={t('app.messaging.subtitle', 'Platform-wide channels & message templates')} />}
      <div className="flex gap-1 border-b border-white/10">
        {(['providers', 'templates'] as const).map((k) => (
          <button key={k} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-primary text-fg' : 'border-transparent text-muted hover:text-fg'}`}>
            {t(`app.messaging.tab.${k}`, k === 'providers' ? 'Channels' : 'Templates')}
          </button>
        ))}
      </div>

      {tab === 'providers' && (
        provs.isLoading ? <div className="tk-card p-4 text-sm text-muted">{t('common:loading', 'Loading…')}</div> :
        <div className="space-y-3">
          {UI_CHANNELS.map((ch) => (
            <ProviderCard key={ch} channel={ch}
              provider={provs.data?.providers.find((p) => p.type === ch)}
              onSaved={() => { qc.invalidateQueries({ queryKey: ['msg-providers'] }); toast.ok(t('app.messaging.saved', 'Saved')); }}
              onTest={async (target) => { const r = await systemApi.testMessagingProvider(ch, target); r.ok ? toast.ok(t('app.messaging.testOk', 'Test sent')) : toast.error(r.error ?? t('app.messaging.testFail', 'Test failed')); }} />
          ))}
        </div>
      )}

      {tab === 'templates' && <TemplateEditor />}
    </div>
  );
}

function ProviderCard({ channel, provider, onSaved, onTest }: {
  channel: string;
  provider?: { name: string | null; isEnabled: boolean; config: Record<string, unknown> };
  onSaved: () => void;
  onTest: (target?: string) => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const isEmail = channel === 'email';
  // Email backend: default to smtp, or whatever was saved.
  const [emailType, setEmailType] = useState<string>(() => {
    const saved = provider?.config?.providerType;
    return typeof saved === 'string' && EMAIL_PROVIDER_TYPES.includes(saved as never) ? saved : 'smtp';
  });
  // Fields shown depend on channel (and, for email, the selected backend).
  const fields: FieldDef[] = isEmail
    ? [...(EMAIL_TYPE_FIELDS[emailType] ?? []), { key: 'fromEmail', label: 'From email' }, { key: 'fromName', label: 'From name (optional)' }]
    : (CHANNEL_FIELDS[channel] ?? []);
  const [cfg, setCfg] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    const cfgObj = provider?.config ?? {};
    // Seed every key any email backend might use, plus the current channel's.
    const allKeys = new Set<string>();
    if (isEmail) { for (const list of Object.values(EMAIL_TYPE_FIELDS)) for (const f of list) allKeys.add(f.key); ['fromEmail', 'fromName', 'host', 'port'].forEach((k) => allKeys.add(k)); }
    for (const f of (CHANNEL_FIELDS[channel] ?? [])) allKeys.add(f.key);
    for (const k of allKeys) { const v = cfgObj[k]; init[k] = v == null ? '' : String(v); }
    return init;
  });
  const [enabled, setEnabled] = useState(provider?.isEnabled ?? false);
  const [testTarget, setTestTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    // Only send changed fields (skip the '********' redaction placeholder).
    const out: Record<string, unknown> = {};
    for (const f of fields) { const v = cfg[f.key]; if (v && v !== '********') out[f.key] = v; }
    // Persist the selected email backend so the connector knows how to send.
    if (isEmail) out.providerType = emailType;
    try { await systemApi.saveMessagingProvider(channel, { isEnabled: enabled, config: out }); onSaved(); }
    finally { setBusy(false); }
  };

  return (
    <div className="tk-card space-y-3 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium capitalize">{channel}</div>
        <label className="flex items-center gap-2 text-xs text-muted">
          {t('app.messaging.enabled', 'Enabled')}
          <button onClick={() => setEnabled((v) => !v)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${enabled ? 'bg-primary' : 'bg-white/10 ring-1 ring-white/15'}`}>
            <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition ${enabled ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </label>
      </div>
      {isEmail && (
        <label className="block text-xs text-muted">
          {t('app.messaging.email.provider', 'Provider')}
          <select value={emailType} onChange={(e) => setEmailType(e.target.value)}
            className="tk-input mt-1 w-full py-1.5 text-sm">
            {EMAIL_PROVIDER_TYPES.map((tpe) => (
              <option key={tpe} value={tpe}>{t(`app.messaging.email.types.${tpe}`, EMAIL_TYPE_LABELS[tpe])}</option>
            ))}
          </select>
        </label>
      )}
      <div className="grid grid-cols-2 gap-2">
        {fields.map((f) => (
          <label key={f.key} className="text-xs text-muted">
            {f.label}
            <input type={f.secret ? 'password' : 'text'} value={cfg[f.key] ?? ''} placeholder={f.secret && provider?.config?.[f.key] === '********' ? '••••••••' : ''}
              onChange={(e) => setCfg((c) => ({ ...c, [f.key]: e.target.value }))}
              className="tk-input mt-1 w-full py-1.5 text-sm" />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <button className="tk-btn-primary px-3 py-1.5 text-sm" disabled={busy} onClick={save}><Save size={14} /> {t('common:actions.save', 'Save')}</button>
        <input value={testTarget} onChange={(e) => setTestTarget(e.target.value)} placeholder={t('app.messaging.testTarget', 'test recipient')} className="tk-input w-48 py-1.5 text-sm" />
        <button className="tk-btn-ghost px-3 py-1.5 text-sm" onClick={() => onTest(testTarget || undefined)}><Send size={14} /> {t('app.messaging.test', 'Test')}</button>
      </div>
    </div>
  );
}

// Self-contained: owns the locale (EN/HE), refetches templates per locale, and
// edits per-channel content. Email supports an HTML-body toggle whose preview
// renders as markup; other channels are plain text. "Reset" clears the saved
// (event, locale) row so the built-in default is served again.
function TemplateEditor() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const toast = useToast();
  const [locale, setLocale] = useState<string>('en');
  const { data: state, isLoading } = useQuery({ queryKey: ['msg-templates', locale], queryFn: () => systemApi.messagingTemplates(locale) });
  const [kind, setKind] = useState<string>('incident.opened');
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});
  const [html, setHtml] = useState<boolean>(false);
  const [preview, setPreview] = useState<Record<string, { subject?: string; body?: string; text?: string; isHtml?: boolean }> | null>(null);
  const [previewing, setPreviewing] = useState(false);

  // The event kind the current `draft` was seeded for. The preview effect gates
  // on this so it never renders the NEWLY-selected kind against the PREVIOUS
  // kind's draft (which showed the wrong content on every event switch).
  const [draftKind, setDraftKind] = useState<string>('');

  // Seed the draft whenever the fetched state, selected event, or locale change.
  const channelsFor = (k: string) => state?.templates.find((x) => x.eventKind === k)?.channels;
  useEffect(() => {
    if (!state) return;
    const k = state.eventKinds.includes(kind) ? kind : (state.eventKinds[0] ?? 'incident.opened');
    if (k !== kind) setKind(k);
    const ch = channelsFor(k);
    setDraft(normaliseChannels(ch));
    setHtml(ch?.email?.isHtml === true);
    setPreview(null);
    setDraftKind(k); // mark the draft as belonging to k
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, kind]);

  const reseed = (k: string) => setKind(k); // effect above re-seeds the draft
  const setField = (ch: string, field: string, val: string) =>
    setDraft((d) => ({ ...d, [ch]: { ...(d[ch] ?? {}), [field]: val } }));

  // Assemble the payload, tagging the email channel with the HTML flag.
  const payload = () => {
    const out: Record<string, Record<string, unknown>> = {};
    for (const ch of UI_CHANNELS) out[ch] = { ...(draft[ch] ?? {}) };
    if (out.email) out.email.isHtml = html;
    return out;
  };
  const save = async () => {
    try { await systemApi.saveMessagingTemplate(kind, payload(), locale); qc.invalidateQueries({ queryKey: ['msg-templates'] }); toast.ok(t('app.messaging.saved', 'Saved')); }
    catch { toast.error(t('app.messaging.saveFailed', 'Could not save')); }
  };
  const reset = async () => {
    try { await systemApi.resetMessagingTemplate(kind, locale); qc.invalidateQueries({ queryKey: ['msg-templates', locale] }); toast.ok(t('app.messaging.resetOk', 'Reset to default')); }
    catch { toast.error(t('app.messaging.resetFailed', 'Could not reset')); }
  };
  // Live preview: re-render (debounced) whenever the draft/kind/locale/html
  // change, so the operator sees the rendered message as they type — no button.
  // A monotonic request id guards against a slow response overwriting a newer one.
  const previewReqId = useRef(0);
  useEffect(() => {
    if (!state || !draft.email) return; // wait until the draft is seeded
    // Only preview once the draft has been re-seeded for the SELECTED kind —
    // otherwise a kind switch renders the new kind against the old draft.
    if (draftKind !== kind) return;
    const reqId = ++previewReqId.current;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const r = await systemApi.previewMessagingTemplate(kind, payload(), locale);
        if (reqId === previewReqId.current) setPreview(r.rendered);
      } catch { /* transient — keep the last good preview */ }
      finally { if (reqId === previewReqId.current) setPreviewing(false); }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, html, kind, locale, state, draftKind]);

  const insertVar = (ch: string, field: string, token: string) =>
    setField(ch, field, `${draft[ch]?.[field] ?? ''}{{${token}}}`);

  if (isLoading || !state) return <div className="tk-card p-4 text-sm text-muted">{t('common:loading', 'Loading…')}</div>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted">{t('app.messaging.event', 'Event')}</span>
          <select value={kind} onChange={(e) => reseed(e.target.value)} className="tk-input w-56 py-1.5 text-sm">
            {state.eventKinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        {/* Language toggle — edits the template row for the selected locale. */}
        <div className="flex items-center gap-1 rounded-md bg-white/5 p-0.5 ring-1 ring-white/10">
          {state.languages.map((lng) => (
            <button key={lng} onClick={() => setLocale(lng)}
              className={`rounded px-2 py-1 text-xs uppercase ${locale === lng ? 'bg-primary text-white' : 'text-muted hover:text-fg'}`}>{lng}</button>
          ))}
        </div>
      </div>

      {UI_CHANNELS.map((ch) => (
        <div key={ch} className="tk-card space-y-2 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium capitalize">{ch}</div>
            {ch === 'email' && (
              <label className="flex items-center gap-1.5 text-xs text-muted">
                <input type="checkbox" checked={html} onChange={(e) => setHtml(e.target.checked)} />
                {t('app.messaging.htmlBody', 'HTML body')}
              </label>
            )}
          </div>
          {/* Side-by-side: editor fields left, live preview right (stacks on narrow). */}
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="space-y-2">
              {(CHANNEL_TEMPLATE_FIELDS[ch] ?? []).map((f) => (
                <label key={f.key} className="block text-xs text-muted">
                  {f.label}{ch === 'email' && f.key === 'body' && html ? ` (${t('app.messaging.html', 'HTML')})` : ''}
                  {f.multiline
                    ? <textarea rows={ch === 'email' && f.key === 'body' && html ? 6 : 3} value={draft[ch]?.[f.key] ?? ''} onChange={(e) => setField(ch, f.key, e.target.value)} className="tk-input mt-1 w-full py-1.5 text-sm font-mono" />
                    : <input value={draft[ch]?.[f.key] ?? ''} onChange={(e) => setField(ch, f.key, e.target.value)} className="tk-input mt-1 w-full py-1.5 text-sm" />}
                </label>
              ))}
            </div>
            <div className="rounded-md bg-black/20 p-2 text-xs">
              <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted">
                {t('app.messaging.preview', 'Preview')}
                {previewing && <Loader2 size={11} className="animate-spin text-muted" />}
              </div>
              {preview?.[ch] ? (
                <>
                  {preview[ch].subject != null && <div className="mb-1 text-muted">{t('app.messaging.subject', 'Subject')}: <span className="text-fg">{preview[ch].subject}</span></div>}
                  {preview[ch].isHtml
                    ? <div className="rounded bg-white p-2 text-black" dangerouslySetInnerHTML={{ __html: preview[ch].body ?? '' }} />
                    : <div className="whitespace-pre-wrap text-fg">{preview[ch].body ?? preview[ch].text}</div>}
                </>
              ) : (
                <div className="text-muted">{t('app.messaging.previewEmpty', '—')}</div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Variable palette */}
      <div className="tk-card p-3 text-xs">
        <div className="mb-1 font-medium text-muted">{t('app.messaging.variables', 'Variables')} — {t('app.messaging.variablesHint', 'click to insert into the first channel body')}</div>
        <div className="flex flex-wrap gap-1">
          {state.variables.flatMap((g) => g.vars).map((v) => (
            <button key={v.token} onClick={() => insertVar(UI_CHANNELS[0], CHANNEL_TEMPLATE_FIELDS[UI_CHANNELS[0]][1]?.key ?? 'body', v.token)}
              title={v.example} className="rounded bg-white/5 px-1.5 py-0.5 text-primary ring-1 ring-white/10 hover:bg-white/10">{`{{${v.token}}}`}</button>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button className="tk-btn-primary px-3 py-1.5 text-sm" onClick={save}><Save size={14} /> {t('common:actions.save', 'Save')}</button>
        <button className="tk-btn-ghost px-3 py-1.5 text-sm text-muted" onClick={reset}>{t('app.messaging.reset', 'Reset to default')}</button>
      </div>
    </div>
  );
}

// Coerce stored channel content into a flat string map for the editor.
function normaliseChannels(channels?: Record<string, { subject?: string; body?: string; text?: string }>): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const ch of UI_CHANNELS) {
    const c = channels?.[ch] ?? {};
    out[ch] = {};
    for (const f of CHANNEL_TEMPLATE_FIELDS[ch] ?? []) out[ch][f.key] = (c as Record<string, string>)[f.key] ?? '';
  }
  return out;
}

// Security — fully functional, but enforcement is gated by SECURITY_2FA_ENABLED
// on the server (shown as a banner). Toggles + TOTP enrollment all work; nothing
// is enforced at login until the env flag is set.
function SecurityPanel() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['security'], queryFn: systemApi.security });
  const refresh = () => qc.invalidateQueries({ queryKey: ['security'] });
  const [enroll, setEnroll] = useState<{ qrDataUrl: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const grafanaUrl = import.meta.env.VITE_GRAFANA_URL as string | undefined;

  if (!data) return <div className="tk-card max-w-2xl p-4 text-sm text-muted">{t('app.security.loading')}</div>;
  const s = data.settings;
  const set = async (patch: Partial<typeof s>) => { await systemApi.saveSecurity(patch); refresh(); };

  const startEnroll = async () => { const r = await systemApi.totpEnroll(); setEnroll({ qrDataUrl: r.qrDataUrl, secret: r.secret }); setMsg(null); };
  const confirmEnroll = async () => {
    try { await systemApi.totpConfirm(code); setEnroll(null); setCode(''); setMsg(t('app.security.msg.enabled')); refresh(); }
    catch { setMsg(t('app.security.msg.invalidCode')); }
  };
  const disable2fa = async () => { await systemApi.totpDisable(); setMsg(t('app.security.msg.disabled')); refresh(); };

  const Toggle = ({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) => (
    <button onClick={() => onChange(!on)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${on ? 'bg-primary' : 'bg-white/10 ring-1 ring-white/15'}`}>
      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition ${on ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center justify-between border-b border-white/5 py-2 last:border-0"><span className="text-sm text-muted">{label}</span>{children}</div>
  );

  return (
    <div className="tk-card max-w-2xl space-y-3 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{t('app.security.title')}</div>
        <span className={`rounded-full px-2 py-0.5 text-xs ${data.enforcementEnabled ? 'bg-ok/15 text-ok' : 'bg-warning/15 text-warning'}`}>
          {data.enforcementEnabled ? t('app.security.enforcementOn') : t('app.security.enforcementOff')}
        </span>
      </div>
      <p className="text-xs text-muted"><Trans t={t} i18nKey="app.security.intro" components={{ code: <span className="tk-num" /> }} /></p>

      {/* My 2FA */}
      <Row label={t('app.security.myTotp')}>
        {data.myTotpEnabled
          ? <button className="tk-btn-ghost px-2 py-1 text-xs text-critical" onClick={disable2fa}>{t('app.security.disable')}</button>
          : <button className="tk-btn-primary px-2 py-1 text-xs" onClick={startEnroll}>{t('app.security.setUp')}</button>}
      </Row>
      {enroll && (
        <div className="rounded-md bg-black/20 p-3 text-center">
          <p className="mb-2 text-xs text-muted">{t('app.security.enroll.scan')}</p>
          <img src={enroll.qrDataUrl} alt={t('app.security.enroll.qrAlt')} className="mx-auto h-40 w-40 rounded bg-white p-1" />
          <p className="mt-2 text-[11px] text-muted">{t('app.security.enroll.manualKey')} <span className="tk-num select-all">{enroll.secret}</span></p>
          <div className="mt-2 flex justify-center gap-2">
            <input className="tk-input w-28 py-1 text-center text-sm tk-num" value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" />
            <button className="tk-btn-primary px-3 py-1 text-xs" onClick={confirmEnroll}>{t('app.security.enroll.confirm')}</button>
          </div>
        </div>
      )}
      {msg && <p className="text-xs text-ok">{msg}</p>}

      {/* Org-wide policy */}
      <div className="border-t border-white/10 pt-2 text-xs font-medium uppercase tracking-wider text-muted/70">{t('app.security.policy')}</div>
      <Row label={t('app.security.requireTwoFactor')}><Toggle on={s.twoFactorRequired} onChange={(v) => set({ twoFactorRequired: v })} /></Row>
      <Row label={t('app.security.tenantMfa')}><Toggle on={s.tenantMfaEnforced} onChange={(v) => set({ tenantMfaEnforced: v })} /></Row>
      <Row label={t('app.security.sessionTimeout')}>
        <select className="tk-input py-1 text-xs" value={s.sessionTimeoutSeconds} onChange={(e) => set({ sessionTimeoutSeconds: Number(e.target.value) })}>
          {[900, 1800, 3600, 14400, 28800].map((sec) => <option key={sec} value={sec}>{sec >= 3600 ? `${sec / 3600}h` : `${sec / 60}m`}</option>)}
        </select>
      </Row>
      <Row label={t('app.security.minPasswordLength')}>
        <select className="tk-input py-1 text-xs" value={s.passwordMinLength} onChange={(e) => set({ passwordMinLength: Number(e.target.value) })}>
          {[8, 10, 12, 14, 16].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </Row>
      <Row label={t('app.security.requireUppercase')}><Toggle on={s.passwordRequireUpper} onChange={(v) => set({ passwordRequireUpper: v })} /></Row>
      <Row label={t('app.security.requireNumber')}><Toggle on={s.passwordRequireNumber} onChange={(v) => set({ passwordRequireNumber: v })} /></Row>
      <Row label={t('app.security.requireSymbol')}><Toggle on={s.passwordRequireSymbol} onChange={(v) => set({ passwordRequireSymbol: v })} /></Row>

      {/* IP allowlist */}
      <div className="border-t border-white/10 pt-2 text-xs font-medium uppercase tracking-wider text-muted/70">{t('app.security.ipAllowlist')}</div>
      <Row label={t('app.security.restrictToAllowlist')}><Toggle on={s.ipAllowlistEnabled} onChange={(v) => set({ ipAllowlistEnabled: v })} /></Row>
      <label className="block text-xs text-muted">{t('app.security.allowedIps')}
        <input className="tk-input mt-1 w-full py-1 text-sm" defaultValue={(s.ipAllowlist ?? []).join(', ')}
          onBlur={(e) => set({ ipAllowlist: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
          placeholder="203.0.113.4, 10.0.0.0/8" />
      </label>

      <p className="border-t border-white/10 pt-2 text-[11px] text-muted">{t('app.security.ssoNote')}</p>
      {!grafanaUrl && <p className="text-[11px] text-muted"><Trans t={t} i18nKey="app.security.grafanaNote" components={{ code: <span className="tk-num" /> }} /></p>}
    </div>
  );
}
